import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { IRadioOption } from '@feathr/components';
import { Alert, Button, Radios } from '@feathr/components';
import FormSelect from '@feathr/extender/components/FormSelect';

import {
  getPeopleFormListEmptyMessage,
  getSavedUpsync,
  isPeopleFormListEmpty,
  stagePeopleFormIds,
} from '../../../RaisersEdgeActivitiesTab/RaisersEdgeActivitiesTab.utils';

enum EFormScope {
  All = 'all',
  Specific = 'specific',
}

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  /** Disables the controls, e.g. while the page saves. */
  disabledReason?: string;
}

/**
 * Chooses which forms' submissions create constituents: all forms (`null`) or a list of forms.
 * Edits are staged for the page's Save & Sync.
 */
function RaisersEdgeFormPicker({ disabledReason, integration }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const upsync = integration.get('upsync_activities');
  const formIds = upsync?.people_form_ids;
  const scope = Array.isArray(formIds) ? EFormScope.Specific : EFormScope.All;
  const isDisabled = !!disabledReason;
  // The list the admin had before choosing All forms, to bring back if they switch again.
  const lastFormIds = useRef<string[]>([]);
  // Undefined until the forms load. The list leaves out archived forms and forms past the page size.
  const [listedFormIds, setListedFormIds] = useState<ReadonlySet<string>>();
  const unlistedFormIds =
    listedFormIds && formIds ? formIds.filter((id) => !listedFormIds.has(id)) : [];

  const scopeOptions: IRadioOption[] = [
    { id: EFormScope.All, name: t('All forms') },
    { id: EFormScope.Specific, name: t('Select specific forms') },
  ];

  function handleScopeChange(value?: string): void {
    if (value === EFormScope.All) {
      lastFormIds.current = formIds ?? [];
      stagePeopleFormIds(integration, null);
      return;
    }
    const savedFormIds = getSavedUpsync(integration)?.people_form_ids;
    stagePeopleFormIds(
      integration,
      lastFormIds.current.length ? lastFormIds.current : savedFormIds ?? [],
    );
  }

  function handleFormsLoad(ids: string[]): void {
    setListedFormIds(new Set(ids));
  }

  // The select only knows listed forms, so it would drop unlisted saved ids on every change.
  function handleFormsChange(value?: string[]): void {
    stagePeopleFormIds(integration, [...unlistedFormIds, ...(value ?? [])]);
  }

  function handleRemoveUnlisted(): void {
    stagePeopleFormIds(
      integration,
      (formIds ?? []).filter((id) => !unlistedFormIds.includes(id)),
    );
  }

  return (
    <Stack gap={theme.spacing['2']}>
      <Radios
        aria-label={t('Forms that create constituents')}
        dataName={'raisers-edge-form-scope'}
        disabled={isDisabled}
        layout={'block'}
        name={'raisers-edge-form-scope'}
        onChange={handleScopeChange}
        options={scopeOptions}
        orientation={'vertical'}
        value={scope}
      />
      {scope === EFormScope.Specific && (
        <FormSelect
          disabled={isDisabled}
          isMulti={true}
          label={t('Form(s)')}
          onChange={handleFormsChange}
          onOptionsLoad={handleFormsLoad}
          placeholder={t('Search forms')}
          showLastModified={true}
          validationError={
            isPeopleFormListEmpty(upsync) ? getPeopleFormListEmptyMessage(t) : undefined
          }
          value={formIds ?? []}
        />
      )}
      {scope === EFormScope.Specific && unlistedFormIds.length > 0 && (
        <Alert
          description={t(
            'Feathr cannot show it here, for example because it is archived. It stays in the list until you remove it.',
            { count: unlistedFormIds.length },
          )}
          title={t('The list has {{count}} form that is not shown', {
            count: unlistedFormIds.length,
          })}
          type={'warning'}
        >
          <Button disabled={isDisabled} onClick={handleRemoveUnlisted} type={'link'}>
            {t('Remove from the list', { count: unlistedFormIds.length })}
          </Button>
        </Alert>
      )}
    </Stack>
  );
}

export default observer(RaisersEdgeFormPicker);
